import { useState, useCallback, type KeyboardEvent } from 'react';
import { Send, Square } from 'lucide-react';
import clsx from 'clsx';

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop: () => void;
  isLoading: boolean;
  disabled?: boolean;
}

export function ChatInput({ onSend, onStop, isLoading, disabled }: ChatInputProps) {
  const [input, setInput] = useState('');

  const handleSend = useCallback(() => {
    if (input.trim() && !disabled) {
      onSend(input);
      setInput('');
    }
  }, [input, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1 relative flex">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask your coach a question..."
          disabled={disabled}
          rows={1}
          className={clsx(
            'w-full resize-none rounded-xl border-2 border-gray-200 px-4 py-3 pr-4',
            'focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500',
            'placeholder-gray-400 text-gray-900',
            'min-h-[52px] max-h-[200px]',
            'transition-all duration-200',
            'shadow-sm',
            disabled && 'bg-gray-50 cursor-not-allowed'
          )}
          style={{
            height: 'auto',
            minHeight: '52px',
          }}
          onInput={e => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = Math.min(target.scrollHeight, 200) + 'px';
          }}
        />
      </div>
      {isLoading ? (
        <button
          onClick={onStop}
          className={clsx(
            'flex items-center justify-center',
            'w-[52px] h-[52px] rounded-xl',
            'bg-red-500 text-white',
            'hover:bg-red-600 active:scale-95 transition-all',
            'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
            'shadow-md'
          )}
          title="Stop generating"
        >
          <Square className="w-5 h-5" />
        </button>
      ) : (
        <button
          onClick={handleSend}
          disabled={!input.trim() || disabled}
          className={clsx(
            'flex items-center justify-center',
            'w-[52px] h-[52px] rounded-xl',
            'bg-gradient-to-br from-orange-500 to-orange-600 text-white',
            'hover:from-orange-600 hover:to-orange-700 active:scale-95 transition-all',
            'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2',
            'disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed disabled:active:scale-100',
            'shadow-md disabled:shadow-none'
          )}
          title="Send message"
        >
          <Send className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
